import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { AppShell } from '../../app/AppShell'
import { NoticeList } from '../../app/NoticeList'
import { ApiError } from '../../api/problem'
import { useCharacterSheet } from '../../domains/characters/queries'
import { CharacterMenu } from '../../domains/characters/CharacterMenu'
import { useCharacterEditing } from '../../domains/characters/useCharacterEditing'
import { AbilitiesCard } from '../../domains/characters/sections/AbilitiesCard'
import { AspectsCard } from '../../domains/characters/sections/AspectsCard'
import { BioCard } from '../../domains/characters/sections/BioCard'
import { FateCard } from '../../domains/characters/sections/FateCard'
import { HarmCard } from '../../domains/characters/sections/HarmCard'
import { EquipmentCard } from '../../domains/characters/sections/EquipmentCard'
import {
  EnchantedItemsCard,
  FocusItemsCard,
  RoteSpellsCard,
  SpecializationsCard,
} from '../../domains/characters/sections/MagicCards'
import { SheetNav } from '../../domains/characters/sections/SheetNav'
import { SkillsCard } from '../../domains/characters/sections/SkillsCard'
import { Banner, Button, Card, IconButton, Lightbox } from '../../design/components'
import styles from './characters.module.css'

/**
 * Design spec §5.1 — the character sheet. Read-only; Stage 5 makes it editable.
 *
 * The card order is the spec's: aspects first (they drive the game), then skills,
 * then stress and consequences, then fate. Powers, magic and equipment collapse
 * below so the top stays scannable.
 */
export function CharacterSheetPage() {
  const params = useParams<{ id: string }>()
  const characterId = Number(params.id)
  const [shapeId, setShapeId] = useState<number | null>(null)
  const [portraitOpen, setPortraitOpen] = useState(false)

  const query = useCharacterSheet(characterId, shapeId)

  // Before the early returns below — hooks cannot be conditional. `canEdit` is
  // the server's word: only the owner may write, and it says so on every sheet.
  const editing = useCharacterEditing(characterId, shapeId, query.data?.isOwner === true)

  if (Number.isNaN(characterId)) {
    return <SheetMessage title="Character" message="That character id is not a number." />
  }

  if (query.isPending) {
    return <SheetMessage title="Character" message="Loading…" subtitle="FETCHING SHEET" />
  }

  if (query.isError) {
    // 404 here also covers "exists, but not yours to see" — the API resolves ids
    // from an access-checked parent so a stranger cannot probe them (§2 of the
    // architecture doc). Either way it is an empty state, not an error.
    const notFound = query.error instanceof ApiError && query.error.kind === 'not-found'
    return (
      <SheetMessage
        title="Character"
        message={
          notFound
            ? 'No such character, or it is not visible to you.'
            : 'Could not load this character.'
        }
        tone={notFound ? 'info' : 'error'}
      />
    )
  }

  const sheet = query.data
  const core = sheet.sections.core
  const bio = sheet.sections.bio
  const shapes = sheet.sections.shapes
  const allShapes = shapes?.shapes ?? []
  const activeShapeId = sheet.shapeId

  // The bio's identity half belongs in the header, with the name — the portrait
  // instead of the generic sigil, and the full name when it adds something the
  // character's name does not. The prose stays in the collapsed card below.
  const portrait = bio?.portraitUrl ?? bio?.portraitUrlSmall ?? null
  // Full screen deserves the original: the sized variants are cut for a tile and a header,
  // and blowing one of those up to fill a laptop is a blur.
  const portraitFull = bio?.portraitUrlOriginal ?? bio?.portraitUrl ?? portrait
  const name = core?.name ?? 'this character'
  const fullName = bio?.fullName ?? null
  const identity = fullName !== null && fullName !== core?.name ? fullName : null

  return (
    <AppShell
      title={core?.name ?? 'Character'}
      subtitle={subtitleFor(core?.type, core?.classType, core?.refreshLevel)}
      avatarUrl={portrait}
      identity={identity}
      headerLeading={<BackToTop />}
      headerAction={
        <CharacterMenu characterId={characterId} name={name} isHidden={sheet.isHidden} />
      }
    >
      {/* The face first, at a size worth looking at — the header's 40px avatar identifies
          the sheet you are on, it does not show you the character. Tapping it goes full
          screen; the gallery is one small step sideways from it, where a reader who wants
          more pictures is already looking.

          The space beside a 120px portrait was empty, and the sheet below it is a dozen
          cards deep — so it holds the map: one button per section, which scrolls to that
          card and opens it if it is collapsed. */}
      <div className={styles.portraitRow}>
        {portrait !== null && (
          <button
            type="button"
            className={styles.portraitButton}
            onClick={() => setPortraitOpen(true)}
            aria-label={`View the portrait of ${name} full screen`}
          >
            <img className={styles.portrait} src={portrait} alt="" />
          </button>
        )}
        {/* Which cards this sheet actually has changes with the data — a shape's powers, a
            magic card that appears the moment an ability grants a discipline, an empty
            section hidden from a reader who cannot add to it. The nav re-reads the page
            whenever any of that could have moved. The gallery rides at the end of the same
            row, so the portrait's neighbours are one arrangement rather than two. */}
        <SheetNav
          revision={`${query.dataUpdatedAt}:${activeShapeId ?? ''}:${String(sheet.isOwner)}`}
          {...(portrait !== null && { galleryTo: `/characters/${characterId}/images` })}
        />
      </div>

      {portraitOpen && portraitFull !== null && (
        <Lightbox
          images={[{ src: portraitFull, alt: `Portrait of ${name}` }]}
          onClose={() => setPortraitOpen(false)}
        />
      )}

      {allShapes.length > 1 && (
        <div className={styles.shapes} role="group" aria-label="Shape">
          {allShapes.map((shape) => (
            <button
              key={shape.id}
              type="button"
              className={`${styles.shapeTab} ${shape.id === activeShapeId ? styles.shapeTabActive : ''}`}
              aria-pressed={shape.id === activeShapeId}
              onClick={() => setShapeId(shape.id ?? null)}
            >
              {shape.name}
            </button>
          ))}
        </div>
      )}

      <NoticeList notices={editing.notices} failure={editing.failure} onDismiss={editing.dismiss} />

      <AspectsCard aspects={sheet.sections.aspects} editing={editing} />
      <SkillsCard skills={sheet.sections.skills} editing={editing} />
      <HarmCard
        stress={sheet.sections.stress}
        consequences={sheet.sections.consequences}
        editing={editing}
      />
      <FateCard fate={sheet.sections.fate} core={core} editing={editing} />

      <AbilitiesCard
        abilities={sheet.sections.abilities}
        shapeId={activeShapeId}
        shapes={allShapes}
        hasSpellcasting={core?.hasSpellcasting === true}
        editing={editing}
      />
      {/* The magic half of the sheet, on one server-computed flag. `hasSpellcasting` is true
          for the spellcasting template and for anyone whose abilities grant a discipline, so
          a mortal who picks up Channelling gets these cards the moment the ability lands —
          the ability cascade returns `core` alongside every budget it moved. */}
      {core?.hasSpellcasting === true && (
        <>
          <SpecializationsCard
            specializations={sheet.sections.specializations}
            spellcasting={core.spellcasting}
            editing={editing}
          />
          <FocusItemsCard focusItems={sheet.sections.focusItems} editing={editing} />
          <EnchantedItemsCard enchantedItems={sheet.sections.enchantedItems} editing={editing} />
          <RoteSpellsCard roteSpells={sheet.sections.roteSpells} editing={editing} />
        </>
      )}
      <EquipmentCard equipment={sheet.sections.equipment} editing={editing} />
      <BioCard bio={sheet.sections.bio} editing={editing} />

      {portrait === null && <GalleryCard characterId={characterId} />}

      {!sheet.isOwner && (
        <p className={styles.note}>
          You are not this character&rsquo;s owner, so this sheet is read-only. Private bio fields
          and aspects are hidden.
        </p>
      )}

      <Link to="/characters" className={styles.backLink}>
        <Button variant="secondary" fullWidth>
          All characters
        </Button>
      </Link>
    </AppShell>
  )
}

/**
 * Back to the top of the sheet, in the header's left-hand slot.
 *
 * Phone only. On a desktop the sheet is a scroll wheel or a Home key away and the window is
 * short enough to see where you are; on a phone a spellcaster's sheet is a dozen cards and
 * several screens of scrolling, and the section nav that got you down there lives at the top.
 *
 * It sits at the far left, as far from the primary navigation as the header allows: the two
 * do different jobs, and a fourth icon next to Sheet · Scene · City would read as a fourth
 * destination. Always present rather than appearing after a scroll — a control that comes and
 * goes is one you have to look for, and at the top of the page it simply does nothing.
 */
function BackToTop() {
  return (
    <IconButton
      label="Back to top"
      icon="top"
      className={styles.backToTop}
      onClick={() =>
        window.scrollTo({
          top: 0,
          behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth',
        })
      }
    />
  )
}

/**
 * The gallery, for a sheet that has no portrait to put an icon beside.
 *
 * It is a screen rather than a section: images are visible to anyone who can read the sheet,
 * and the gallery's own `canManage` decides who may change them. Once a character has a
 * portrait, the icon at the top of the sheet is the way in and this card goes away — two
 * links to one screen is one too many.
 */
function GalleryCard({ characterId }: { characterId: number }) {
  return (
    <Card variant="magic" label="More">
      <div className={styles.formActions}>
        <Link to={`/characters/${characterId}/images`} className={styles.cardLink}>
          <Button variant="secondary" fullWidth>
            Gallery
          </Button>
        </Link>
      </div>
    </Card>
  )
}

function SheetMessage({
  title,
  subtitle,
  message,
  tone = 'info',
}: {
  title: string
  subtitle?: string
  message: string
  tone?: 'info' | 'error'
}) {
  return (
    <AppShell title={title} {...(subtitle !== undefined && { subtitle })}>
      <Card>
        <Banner tone={tone}>{message}</Banner>
      </Card>
      <Link to="/characters" className={styles.backLink}>
        <Button variant="secondary" fullWidth>
          All characters
        </Button>
      </Link>
    </AppShell>
  )
}

/**
 * e.g. "PC · SPELLCASTER · REFRESH 10" — the spec's template + mantle line (§5.1).
 *
 * Refresh rather than a level: the system has no character levels, and the one the sheet used
 * to print here was a number nothing in play could spend.
 */
function subtitleFor(
  type: string | undefined,
  classType: string | undefined,
  refresh: number | undefined,
): string | undefined {
  const parts = [type, classType, refresh === undefined ? undefined : `Refresh ${refresh}`].filter(
    (part): part is string => part !== undefined && part !== '',
  )
  return parts.length === 0 ? undefined : parts.join(' · ')
}
